import { all, takeLeading, call, put } from '@redux-saga/core/effects';
import axios from 'axios';

import { ERROR } from '../../../utils/constants';
import { getErrorMessage } from '../../../utils/api-util';

import {
  requestRetrieveAccounts,
  requestFindAccount,
  requestReviewCompanyDocuments,
  requestUploadCompanyDocument,
} from './admin-account.actions';
import {
  getRetrieveAccountsRequest,
  getFindAccountRequest,
  getReviewCompanyDocumentsRequest,
  getUploadCompanyDocumentRequest,
} from './admin-account.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../../slices/system/system.slice';
import { setAccount, setAccounts } from '../../../slices/admin/account-management/account-management.slice';

export function* performRetrieveAccountsRequest({ payload }) {
  const { filters, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Accounts'));

    const [endpoint, options] = getRetrieveAccountsRequest(filters);

    const { data } = yield call(axios, endpoint, options);
    const { accounts, totalNumberOfAccounts } = data;

    if (onSuccess) {
      yield call(onSuccess, totalNumberOfAccounts);
    }

    yield put(setAccounts(accounts));
  } catch (error) {
    yield put(setAccounts([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Accounts'));
  }
}

export function* watchForRetrieveAccountsRequest() {
  yield takeLeading(requestRetrieveAccounts.type, performRetrieveAccountsRequest);
}

export function* performFindAccountRequest({ payload }) {
  const { organisationExternalReference } = payload;

  try {
    yield put(addSpinner('Retrieving Account'));

    const [endpoint, options] = getFindAccountRequest(organisationExternalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setAccount(data));
  } catch (error) {
    yield put(setAccount(undefined));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Account'));
  }
}

export function* watchForFindAccountRequest() {
  yield takeLeading(requestFindAccount.type, performFindAccountRequest);
}

export function* performReviewCompanyDocumentsRequest({ payload }) {
  const { document, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Document'));

    const [endpoint, options] = getReviewCompanyDocumentsRequest(document);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }
  } catch (error) {
    yield put(setAccount(undefined));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Document'));
  }
}

export function* watchForReviewCompanyDocumentsRequest() {
  yield takeLeading(requestReviewCompanyDocuments.type, performReviewCompanyDocumentsRequest);
}

export function* performUploadCompanyDocumentRequest({ payload }) {
  const { document, onSuccess } = payload;

  try {
    yield put(addSpinner('Uploading Document'));

    const [endpoint, options] = getUploadCompanyDocumentRequest(document);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }
  } catch (error) {
    yield put(setAccount(undefined));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Uploading Document'));
  }
}

export function* watchForUploadCompanyDocumentRequest() {
  yield takeLeading(requestUploadCompanyDocument.type, performUploadCompanyDocumentRequest);
}

export default function* adminAccountSaga() {
  yield all([
    watchForRetrieveAccountsRequest(),
    watchForFindAccountRequest(),
    watchForReviewCompanyDocumentsRequest(),
    watchForUploadCompanyDocumentRequest(),
  ]);
}
